// WebView JS Bridge — 业务层组合入口
// 原生注入已取消，改为使用浏览器标准 API：
//   - platform.js      : 平台检测、Promise 超时包装器、Store 更新
//   - web-bluetooth.js : Web Bluetooth API（navigator.bluetooth）
//   - web-api.js       : 其他 Web 标准 API（定位、权限、用户信息等）
//   - pc-mock.js       : PC 环境模拟实现（开发调试）

import { isPC, withTimeout, updateStore } from '../di/platform'
import * as webBT from '../di/web-bluetooth'
import * as webAPI from '../di/web-api'
import {
  pcOpenBT,
  pcConnectDevice,
  pcDisconnectDevice,
  pcListBTItems,
  pcCallHandler,
  pcRegisterHandler,
  pcWatchBtstate,
  pcWatchBTStatus,
  pcWatchVIN,
  pcGetLocation,
  pcGetUserInfo,
  pcGetLocationAuth,
  pcUploadInfo,
  pcSetMileage,
  pcGET_MVCI_LIST,
  pcADD_SERVICE_DATA
} from '../di/pc-mock'

// 蓝牙相关工具方法
const bridgeUtil = {
  // 是否为PC浏览器环境
  isPC() {
    return isPC
  },

  isAndroid() {
    const ua = navigator.userAgent
    return ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1
  },

  isIPhone() {
    const ua = navigator.userAgent
    return !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  },

  // 是否有蓝牙支持（Web Bluetooth 或 PC 模拟）
  hasNativeSupport() {
    return isPC || webBT.isSupported()
  },

  callHandler(name, data, callback) {
    if (isPC) {
      pcCallHandler(name, data, callback)
      return
    }
    webAPI.callHandler(name, data, callback)
  },

  registerHandler(name, callback) {
    if (isPC) {
      pcRegisterHandler(name)
      return
    }
    webAPI.registerHandler(name, callback)
  },

  // 打开蓝牙
  openBT() {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcOpenBT()
        resolve(res)
        return
      }
      if (webBT.isSupported()) {
        try {
          const res = await webBT.openBT()
          resolve(res)
        } catch (err) {
          resolve({ result: '2', msg: err.message })
        }
        return
      }
      const detail = webBT.getSupportDetail()
      resolve({ result: '2', msg: detail.reason })
    }), 5000, { result: '2', msg: '打开蓝牙超时' })
  },

  // 连接蓝牙设备
  connectDevice(device) {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcConnectDevice(device)
        resolve(res)
        return
      }
      if (webBT.isSupported()) {
        try {
          const res = await webBT.connectDevice(device)
          resolve(res)
        } catch (err) {
          resolve({ result: '3', msg: err.message })
        }
        return
      }
      const detail = webBT.getSupportDetail()
      resolve({ result: '3', msg: detail.reason })
    }), 10000, { result: '3', msg: '连接设备超时' })
  },

  // 断开蓝牙设备
  disconnectDevice() {
    if (isPC) {
      return pcDisconnectDevice()
    }
    if (webBT.isSupported()) {
      return webBT.disconnectDevice()
    }
    return Promise.resolve({ code: '00' })
  },

  // 获取蓝牙设备列表
  // Web Bluetooth 模式下会弹出系统设备选择框
  listBTItems() {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcListBTItems()
        resolve(res)
        return
      }
      if (webBT.isSupported()) {
        try {
          const device = await webBT.requestDevice()
          resolve({
            code: '00',
            result: '00',
            data: JSON.stringify([device]),
            msg: '设备已选择'
          })
        } catch (err) {
          if (err.name === 'NotFoundError') {
            // 用户取消选择
            resolve({ code: '00', result: '00', data: '[]', msg: '用户取消' })
          } else {
            resolve({ result: '2', msg: err.message })
          }
        }
        return
      }
      const detail = webBT.getSupportDetail()
      resolve({ result: '2', msg: detail.reason })
    }), 30000, { result: '2', msg: '请求设备超时' })
  },

  // 监听蓝牙状态变化
  watchBtstate() {
    if (isPC) {
      pcWatchBtstate()
      return
    }
    webBT.watchBtstate()
  },

  // 监听蓝牙连接状态
  watchBTStatus() {
    if (isPC) {
      pcWatchBTStatus()
      return
    }
    webBT.watchBTStatus()
  },

  // 监听VIN读取完成
  watchVIN() {
    if (isPC) {
      pcWatchVIN()
      return
    }
    webBT.watchVIN()
  },

  // 获取位置信息
  getLocation() {
    if (isPC) {
      pcGetLocation()
      return
    }
    webAPI.getLocation()
  },

  // 获取用户信息
  getUserInfo() {
    if (isPC) {
      pcGetUserInfo()
      return
    }
    webAPI.getUserInfo()
  },

  // 改变状态栏颜色
  changeBarColor(color) {
    if (isPC) return
    webAPI.changeBarColor(color)
  },

  // 获取位置权限
  getLocationAuth() {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcGetLocationAuth()
        resolve(res)
        return
      }
      const res = await webAPI.getLocationAuth()
      resolve(res)
    }), 3000, { granted: false, msg: '超时' })
  },

  // 上传信息
  uploadInfo(data) {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcUploadInfo(data)
        resolve(res)
        return
      }
      const res = await webAPI.uploadInfo(data)
      resolve(res)
    }), 5000, { code: '2', msg: '上传超时' })
  },

  // 设置里程
  setMileage(mileage) {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcSetMileage(mileage)
        resolve(res)
        return
      }
      const res = await webAPI.setMileage(mileage)
      resolve(res)
    }), 5000, { code: '2', msg: '设置里程超时' })
  },

  // 获取MVCI列表
  GET_MVCI_LIST(params) {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcGET_MVCI_LIST()
        resolve(res)
        return
      }
      const res = await webAPI.GET_MVCI_LIST()
      resolve(res)
    }), 5000, [])
  },

  // Toast提示
  toastTip(msg) {
    if (window.Vue && window.Vue.prototype.$toast) {
      window.Vue.prototype.$toast(msg)
    }
  },

  // 添加服务数据
  ADD_SERVICE_DATA(data) {
    return withTimeout(() => new Promise(async (resolve) => {
      if (isPC) {
        const res = await pcADD_SERVICE_DATA(data)
        resolve(res)
        return
      }
      const res = await webAPI.ADD_SERVICE_DATA(data)
      resolve(res)
    }), 5000, { code: '2', msg: '超时' })
  },

  // 节流函数
  throttle(fn, delay) {
    let last = 0
    return function(...args) {
      const now = Date.now()
      if (now - last > delay) {
        last = now
        fn.apply(this, args)
      }
    }
  },

  // 过滤设备名称
  filterByName(name) {
    if (!name) return ''
    return name.replace(/MVCI_/g, '').replace(/_/g, ' ')
  }
}

export default bridgeUtil
